import { MapPin, Car, Navigation, Clock, Phone, MessageCircle, ArrowUpRight, Footprints, Train, Building2, Info } from "lucide-react";
import { motion } from "framer-motion";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";
import { DirectionsWidget } from "@/components/DirectionsWidget";
import { ENTRANCE, REGISTERED, buildDirectionsUrl, buildMapsPlaceUrl, trackDirectionsClick } from "@/lib/location-links";

/** Verified yard-entrance coordinates — single source for the embedded map. */
const ENTRANCE_LAT = ENTRANCE.lat;
const ENTRANCE_LNG = ENTRANCE.lng;
const ENTRANCE_LABEL = ENTRANCE.fullAddress;

/**
 * Premium "Find the Workshop" section.
 * Anchors the homepage with a verified address card, an embedded map of the
 * yard entrance, an interactive directions widget, and crystal-clear arrival
 * instructions for first-time visitors.
 */
export function FindUs() {
  const biz = getBusinessConfig();

  const directions = [
    {
      icon: Car,
      label: "By car",
      lines: [
        `Sat-nav: ${ENTRANCE_LABEL}`,
        "Approach via Burdett Road or East India Dock Road",
        "Pull through the gated yard entrance — workshop straight ahead",
      ],
    },
    {
      icon: Train,
      label: "By tube / DLR",
      lines: [
        "Limehouse DLR — 7 min walk",
        "Westferry DLR — 9 min walk",
        "Mile End (Central, District, H&C) — 12 min walk",
      ],
    },
    {
      icon: Footprints,
      label: "On arrival",
      lines: [
        "Pull into the yard at 14 Copenhagen Place",
        "Reception is straight ahead — beep the horn if the shutter is down",
        "We'll greet you and book your vehicle in",
      ],
    },
  ];

  return (
    <section id="find-us" className="relative py-20 md:py-28 bg-gradient-to-b from-background via-secondary/30 to-background">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* Heading */}
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Find the workshop</p>
          <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight text-foreground">
            Tucked away off Copenhagen Place — easy once you know where to look.
          </h2>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl">
            We're set back from the road in a private workshop yard. First visit? Use the directions below — most drivers are pulling into the bay within five minutes of arriving in E14.
          </p>
        </div>

        {/* Map + address card */}
        <div className="mt-12 grid lg:grid-cols-[1.35fr_1fr] gap-6 items-stretch">
          {/* Map panel */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden border border-border shadow-2xl shadow-black/[0.08] bg-card aspect-[4/3] lg:aspect-auto lg:min-h-[440px]"
          >
            <iframe
              title={`Map showing the ${biz.name} yard entrance at ${ENTRANCE_LABEL}`}
              src={`https://www.google.com/maps?q=${ENTRANCE_LAT},${ENTRANCE_LNG}(${encodeURIComponent("Woodseer Autos — yard entrance")})&z=18&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 h-full w-full"
              allowFullScreen
            />
            {/* Floating address pill */}
            <div className="pointer-events-none absolute left-4 top-4 right-4 sm:right-auto sm:max-w-[18rem]">
              <div className="pointer-events-auto rounded-2xl bg-card/95 backdrop-blur-md border border-border shadow-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">Yard entrance</p>
                    <p className="mt-0.5 text-sm font-bold text-foreground leading-tight">
                      14 Copenhagen Place<br />London E14 7DF
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-secondary/60 border border-border/60 px-3 py-2">
                  <p className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground">Registered</p>
                  <p className="text-[11px] text-foreground font-medium">{biz.address}, {biz.postcode}</p>
                  <p
                    className="mt-1.5 inline-flex items-start gap-1 text-[10px] text-muted-foreground leading-snug cursor-help"
                    title="Pixley Street is our legal/registered address. The actual workshop yard you drive into is round the corner at 14 Copenhagen Place. Use Copenhagen Place for sat-nav."
                  >
                    <Info className="h-2.5 w-2.5 shrink-0 mt-0.5 text-primary/70" />
                    <span>Legal address — sat-nav to Copenhagen Pl instead.</span>
                  </p>
                </div>
                <a
                  href={buildMapsPlaceUrl("entrance")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("maps_open_click", { location: "find_us_pill", source: "find_us", destination: "entrance" })}
                  className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-primary/80 transition-colors"
                >
                  Open in Google Maps <ArrowUpRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right column: contact + entrance card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border border-border bg-card shadow-xl shadow-black/[0.04] p-6 md:p-8 flex flex-col"
          >
            <div>
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">The entrance</p>
              <h3 className="mt-2 text-xl md:text-2xl font-bold text-foreground tracking-tight">
                Through the driveway off Copenhagen Place
              </h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {biz.entrance}
              </p>
            </div>

            {/* Quick facts grid */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Fact label="Postcode" value={biz.postcode} mono />
              <Fact label="MOT VTS" value={biz.vtsNumber} mono />
              <Fact label="Parking" value="On-site bays" />
              <Fact label="Wait area" value="Indoor seating" />
            </div>

            {/* Hours */}
            <div className="mt-6 rounded-2xl bg-secondary/60 border border-border/60 p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-foreground">
                <Clock className="h-3.5 w-3.5 text-primary" /> Opening hours
              </div>
              <dl className="mt-3 space-y-1.5">
                {biz.hours.map((h) => (
                  <div key={h.day} className="flex items-center justify-between text-sm">
                    <dt className="text-muted-foreground">{h.day}</dt>
                    <dd className="font-semibold text-foreground tabular-nums">{h.time}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "find_us" })}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
              >
                <Phone className="h-4 w-4" /> Call {biz.phone}
              </a>
              <a
                href={buildDirectionsUrl("entrance")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDirectionsClick({ source: "find_us", kind: "entrance", action: "directions_cta" })}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40 hover:text-primary transition-all"
              >
                <Navigation className="h-4 w-4" /> Directions to yard
              </a>
            </div>
            {/* Secondary deep links — registered address + WhatsApp */}
            <div className="mt-2 grid grid-cols-2 gap-2">
              <a
                href={buildDirectionsUrl("registered")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDirectionsClick({ source: "find_us", kind: "registered", action: "directions_cta" })}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-transparent px-3 py-2.5 text-[11px] font-bold text-muted-foreground hover:text-primary hover:border-primary/40 transition-all"
              >
                <Building2 className="h-3.5 w-3.5" /> Registered ({REGISTERED.postcode})
              </a>
              <a
                href={biz.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "find_us" })}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 px-3 py-2.5 text-[11px] font-bold text-[#1ea952] hover:bg-[#25D366]/10 transition-all"
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp a photo
              </a>
            </div>
          </motion.div>
        </div>

        {/* Directions trio */}
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          {directions.map((d, i) => (
            <motion.div
              key={d.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 * i }}
              className="rounded-2xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <d.icon className="h-4.5 w-4.5" />
              </div>
              <h4 className="mt-3 text-sm font-bold text-foreground">{d.label}</h4>
              <ul className="mt-2 space-y-1">
                {d.lines.map((line, idx) => (
                  <li key={idx} className="text-xs text-muted-foreground leading-relaxed flex gap-1.5">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-primary/60 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Interactive directions widget */}
        <div className="mt-10 grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
          <div className="lg:pt-4">
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Plan your route</p>
            <h3 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
              Tap once and we'll route you straight to the yard.
            </h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-md">
              Choose driving, walking or transit. We'll open Google Maps with the verified yard entrance pre-loaded — so the pin lands at the gate, not down the wrong end of Pixley Street.
            </p>
          </div>
          <DirectionsWidget source="find_us" title="Route to the yard entrance" />
        </div>
      </div>
    </section>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-background/50 p-3">
      <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
      <p className={`mt-1 text-sm font-bold text-foreground ${mono ? "font-mono tracking-tight" : ""}`}>{value}</p>
    </div>
  );
}
